import { FormattedMessage } from 'react-intl';
import HOUSEHOLD_DESIGNATION_CODES from 'constants/householdDesignationCodes';
import moment from 'moment';
import APPLICANT_TYPES from 'constants/applicantTypes';
import { deleteEmptyProperties } from 'utils';
import { formatDateDB } from 'utils/timeProvider';
import { ADDITIONAL_MINORS_FIELD_NAMES } from 'pages/AffordaHousehold/AffordaHousehold.AdditionalMinors.constants';
import STUDENT_STATUS from 'constants/studentStatus';
import AFFORDABLE_RELATIONSHIPS from 'constants/affordableRelationships';
import { createFormId } from 'utils/affordaPortal';
import { HOUSEHOLD_SECTION_NAMES } from 'pages/AffordaHousehold/constants';

export const getMemberFieldId = (memberId) =>
  createFormId(HOUSEHOLD_SECTION_NAMES.MEMBERS, memberId);

export const getMemberName = (member) => {
  if (!member?.firstName?.length) return '';
  return `${member.firstName} ${member?.lastName}`.trim();
};

export const getMemberNameById = (members, memberId) => {
  const member = members?.find((m) => m.id === memberId);
  return member ? getMemberName(member) : '';
};

export const getMemberKey = (member) => {
  return member.id;
};

export const getReactHookFormDetails = (_fields) => {
  const allFields = Object.values(_fields).reduce((acc, field) => {
    if (field) {
      Object.values(field).forEach(({ _f: { name, mount, required } }) => {
        acc.push({ required, mount, name });
      });
    }
    return acc;
  }, []);
  return {
    requiredFields: allFields
      .filter(({ required }) => required?.value || required)
      .map(({ name }) => name),
    visibleFields: allFields
      .filter(({ mount }) => mount === true)
      .map(({ name }) => name),
  };
};
export const sortMembers = (members, currentApplicantId) =>
  members.sort((a, b) => {
    // Pets should always be last
    const isPetA = a.applicantType === APPLICANT_TYPES.PET;
    const isPetB = b.applicantType === APPLICANT_TYPES.PET;
    if (isPetA && !isPetB) return 1; // Push "a" (a pet) to the bottom
    if (isPetB && !isPetA) return -1; // Push "b" (a pet) to the bottom

    // Head of household or isProspect=true (isPrimaryUser) should always be first
    const isHeadA =
      a.householdDesignation ===
        HOUSEHOLD_DESIGNATION_CODES.HEAD_OF_HOUSEHOLD || isPrimaryMember(a);
    const isHeadB =
      b.householdDesignation ===
        HOUSEHOLD_DESIGNATION_CODES.HEAD_OF_HOUSEHOLD || isPrimaryMember(b);
    if (isHeadA && !isHeadB) return -1; // "a" is head, sort it higher
    if (isHeadB && !isHeadA) return 1; // "b" is head, sort it higher

    // Spouse should always be second
    const isSpouseA =
      a.householdDesignation === HOUSEHOLD_DESIGNATION_CODES.SPOUSE;
    const isSpouseB =
      b.householdDesignation === HOUSEHOLD_DESIGNATION_CODES.SPOUSE;
    if (isSpouseA && !isSpouseB) return -1; // "a" is spouse, sort it higher
    if (isSpouseB && !isSpouseA) return 1; // "b" is spouse, sort it higher

    // Current user should take precedence over others
    const isUserA = a.applicantId === currentApplicantId;
    const isUserB = b.applicantId === currentApplicantId;
    if (isUserA && !isUserB) return -1; // "a" is the current user
    if (isUserB && !isUserA) return 1; // "b" is the current user

    // Minors should come after adults
    const isMinorA = a.applicantType === APPLICANT_TYPES.MINOR;
    const isMinorB = b.applicantType === APPLICANT_TYPES.MINOR;
    if (isMinorA && !isMinorB) return 1; // "a" is minor, sort lower
    if (isMinorB && !isMinorA) return -1; // "b" is minor, sort lower

    // Default to maintaining the current order if none of the above applies
    return 0;
  });

export const getAdultMembers = (members, options = {}) => {
  const { includeCaretakers = true } = options;

  if (!Array.isArray(members)) {
    return [];
  }

  return members.filter((member) => {
    if (isMinor(member)) {
      return false;
    }

    if (isPet(member)) {
      return false;
    }

    return !(getIsLiveInCaretaker(member) && !includeCaretakers);
  });
};

export const getMembersToInvite = (members, affordableRelationshipsMap) => {
  return members.filter((member) => {
    if (isPrimaryMember(member)) {
      return false;
    }

    if (isMinor(member)) {
      return false;
    }

    if (isPet(member)) {
      return false;
    }

    if (getIsLiveInCaretaker(member)) {
      return false;
    }

    const resolvedRelationship =
      affordableRelationshipsMap?.[member.affordableRelationshipId] ??
      member.affordableRelationship;

    return ![
      AFFORDABLE_RELATIONSHIPS.LIVE_IN_CARETAKER,
      AFFORDABLE_RELATIONSHIPS.CHILD,
      AFFORDABLE_RELATIONSHIPS.FOSTER_CHILD_ADULT,
      AFFORDABLE_RELATIONSHIPS.UNBORN_CHILD,
    ].includes(resolvedRelationship);
  });
};

export const checkCanInviteOthers = (members, affordableRelationshipsMap) => {
  if (!Array.isArray(members)) {
    return false;
  }

  const membersToInvite = getMembersToInvite(
    members,
    affordableRelationshipsMap,
  );

  return membersToInvite.length > 0;
};

export const isMinor = (member) =>
  member?.applicantType === APPLICANT_TYPES.MINOR;

export const isPet = (member) => member?.applicantType === APPLICANT_TYPES.PET;

export const isUnbornMinor = (member) =>
  member?.applicantType === APPLICANT_TYPES.MINOR &&
  member?.name?.toLowerCase()?.includes('unborn');

export const getIsFosterAdult = (member) => {
  return member?.qualifications?.affordableRelationship?.includes(
    AFFORDABLE_RELATIONSHIPS.FOSTER_CHILD_ADULT,
  );
};

export const getIsLiveInCaretaker = (member, affordableRelationshipsMap) =>
  member?.applicantType === APPLICANT_TYPES.LIVE_IN_CARETAKER ||
  member?.householdDesignation ===
    HOUSEHOLD_DESIGNATION_CODES.LIVE_IN_CARETAKER ||
  [AFFORDABLE_RELATIONSHIPS.LIVE_IN_CARETAKER]?.includes(
    affordableRelationshipsMap?.[member?.affordableRelationshipId],
  );

export const getHeadOfHousehold = (members = []) =>
  members?.find(
    ({ householdDesignation }) =>
      householdDesignation === HOUSEHOLD_DESIGNATION_CODES.HEAD_OF_HOUSEHOLD,
  ) ||
  members?.find(
    ({ qualifications }) =>
      qualifications?.affordableRelationship ===
      AFFORDABLE_RELATIONSHIPS.HEAD_OF_HOUSEHOLD,
  );

export const getIsHeadOfHousehold = (member) =>
  member?.householdDesignation ===
  HOUSEHOLD_DESIGNATION_CODES.HEAD_OF_HOUSEHOLD;

export const getIsStudent = (member) =>
  Boolean(
    member?.studentStatus?.length &&
      member?.studentStatus !== STUDENT_STATUS.NOT_A_STUDENT.value,
  );

export const getMinorMembers = (members) => members?.filter(isMinor);

export const isPrimaryMember = (member) =>
  member.isProspect || member.isPrimaryProspect || member.isPrimary;

export const getSecondaryAdultMembers = (members) => {
  return getAdultMembers(members).filter((member) => !isPrimaryMember(member));
};

export const getPrimaryMember = (members) => members.find(isPrimaryMember);

export const calculateAge = (birthday) => {
  const birthDate = moment(birthday);
  const currentDate = moment();
  return currentDate.diff(birthDate, 'years');
};

export const getFormattedName = (member, members, intl, showLastName) => {
  const hasDuplicateFirstName = members.filter(
    (m) => m.firstName === member.firstName && m.id !== member.id,
  ).length;

  const hasDuplicateFirstAndLastName = members.filter(
    (m) =>
      m.firstName === member.firstName &&
      m.lastName === member.lastName &&
      m.id !== member.id,
  ).length;

  const memberName = showLastName ? getMemberName(member) : member.firstName;

  if (hasDuplicateFirstAndLastName) {
    if (intl) {
      return intl.formatMessage(
        {
          id: 'afforda.household.utils.age',
        },
        {
          name: memberName,
          age: calculateAge(member.birthday),
        },
      );
    }
    return (
      <FormattedMessage
        id="afforda.household.utils.age"
        values={{
          name: memberName,
          age: calculateAge(member.birthday),
        }}
      />
    );
  }

  if (hasDuplicateFirstName) {
    return getMemberName(member);
  }

  return memberName;
};

export const getFormattedNames = (members) => {
  // Group members by first name
  const groupedMembers = members.reduce((acc, member) => {
    if (member.applicantType !== APPLICANT_TYPES.MINOR) {
      const firstName = member.firstName;
      if (!acc[firstName]) {
        acc[firstName] = [];
      }
      acc[firstName].push(member);
    }
    return acc;
  }, {});

  const formattedMembers = Object.values(groupedMembers)
    .map((group) => {
      // If there are multiple members with the same first name, add age to the label
      if (group.length > 1) {
        return group.map((member) => ({
          value: getMemberKey(member),
          label: `${member.firstName} (Age ${calculateAge(member.birthday)})`,
        }));
      } else {
        // If there is only one member with this first name, don't add age to the label
        return group.map((member) => ({
          value: getMemberKey(member),
          label: getMemberName(member),
        }));
      }
    })
    .flat();

  return formattedMembers;
};

export const getFormattedMinorNames = (members) => {
  // Group members by first name
  const groupedMembers = members.reduce((acc, member) => {
    if (member.applicantType === APPLICANT_TYPES.MINOR) {
      const firstName = member.firstName;
      if (!acc[firstName]) {
        acc[firstName] = [];
      }
      acc[firstName].push(member);
    }
    return acc;
  }, {});

  const formattedMembers = Object.values(groupedMembers)
    .map((group) => {
      // If there are multiple members with the same first name, add age to the label
      if (group.length > 1) {
        return group.map((member) => ({
          value: getMemberKey(member),
          label: `${member.firstName} (Age ${calculateAge(member.birthday)})`,
        }));
      } else {
        // If there is only one member with this first name, don't add age to the label
        return group.map((member) => ({
          value: getMemberKey(member),
          label: getMemberName(member),
        }));
      }
    })
    .flat();

  return formattedMembers;
};

export const prepareAdditionalMinorsForSubmit = (values) => {
  const EMPTY_VALUES = {
    [ADDITIONAL_MINORS_FIELD_NAMES.pregnancyCount]: '',
    [ADDITIONAL_MINORS_FIELD_NAMES.pregnancyDueDate]: '',
    [ADDITIONAL_MINORS_FIELD_NAMES.adoptionCount]: '',
    [ADDITIONAL_MINORS_FIELD_NAMES.anticipatedAdoptionDate]: '',
    [ADDITIONAL_MINORS_FIELD_NAMES.guardianshipCount]: '',
    [ADDITIONAL_MINORS_FIELD_NAMES.anticipatedGuardianshipStartDate]: '',
  };

  const isNotApplicable = values.expectingMinorsType?.includes(
    ADDITIONAL_MINORS_FIELD_NAMES.notApplicable,
  );
  const isApplicable =
    !isNotApplicable && values.expectingMinorsType?.length > 0;

  let newValues = { ...values };

  const resetFieldValues = (fields) => {
    fields.forEach((field) => {
      newValues[field] = '';
    });
  };

  if (isNotApplicable) {
    newValues = {
      [ADDITIONAL_MINORS_FIELD_NAMES.expectingMinorsType]:
        newValues.expectingMinorsType ?? [],
      ...EMPTY_VALUES,
    };
  } else if (isApplicable) {
    if (
      !values.expectingMinorsType.includes(
        ADDITIONAL_MINORS_FIELD_NAMES.pregnancy,
      )
    ) {
      resetFieldValues([
        ADDITIONAL_MINORS_FIELD_NAMES.pregnancyCount,
        ADDITIONAL_MINORS_FIELD_NAMES.pregnancyDueDate,
      ]);
    }
    if (
      !values.expectingMinorsType.includes(
        ADDITIONAL_MINORS_FIELD_NAMES.adoption,
      )
    ) {
      resetFieldValues([
        ADDITIONAL_MINORS_FIELD_NAMES.adoptionCount,
        ADDITIONAL_MINORS_FIELD_NAMES.anticipatedAdoptionDate,
      ]);
    }
    if (
      !values.expectingMinorsType.includes(
        ADDITIONAL_MINORS_FIELD_NAMES.guardianship,
      )
    ) {
      resetFieldValues([
        ADDITIONAL_MINORS_FIELD_NAMES.guardianshipCount,
        ADDITIONAL_MINORS_FIELD_NAMES.anticipatedGuardianshipStartDate,
      ]);
    }
  }

  const parsedValues = deleteEmptyProperties(newValues);
  const additionalMinors = deleteEmptyProperties({
    expectingMinorsType: parsedValues.expectingMinorsType,
    adoptionCount: parsedValues.adoptionCount
      ? parseInt(parsedValues.adoptionCount)
      : null,
    guardianshipCount: parsedValues.guardianshipCount
      ? parseInt(parsedValues.guardianshipCount)
      : null,
    pregnancyCount: parsedValues.pregnancyCount
      ? parseInt(parsedValues.pregnancyCount)
      : null,
    anticipatedAdoptionDate: parsedValues.anticipatedAdoptionDate
      ? formatDateDB(parsedValues.anticipatedAdoptionDate)
      : null,
    anticipatedGuardianshipStartDate:
      parsedValues.anticipatedGuardianshipStartDate
        ? formatDateDB(parsedValues.anticipatedGuardianshipStartDate)
        : null,
    pregnancyDueDate: parsedValues.pregnancyDueDate
      ? formatDateDB(parsedValues.pregnancyDueDate)
      : null,
  });

  return additionalMinors;
};
